import { NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { ContentBlock, ContentBlockType } from '../model';
import { AccordionBlockRendererComponent } from './block-renderers/accordion-block/accordion-block-renderer.component';
import { CalloutBlockRendererComponent } from './block-renderers/callout-block/callout-block-renderer.component';
import { ColumnsBlockRendererComponent } from './block-renderers/columns-block/columns-block-renderer.component';
import { CustomComponentBlockRendererComponent } from './block-renderers/custom-component-block/custom-component-block-renderer.component';
import { SimpleCalloutBlockRendererComponent } from './block-renderers/simple-callout-block/simple-callout-block-renderer.component';
import { ImageBlockRendererComponent } from './block-renderers/image-block/image-block-renderer.component';
import { MarkdownBlockRendererComponent } from './block-renderers/markdown-block/markdown-block-renderer.component';
import { SimpleSolverBlockRendererComponent } from './block-renderers/simple-solver-block/simple-solver-block-renderer.component';
import { SolverBlockRendererComponent } from './block-renderers/solver-block/solver-block-renderer.component';
import { SuperLatexBlockRendererComponent } from './block-renderers/superlatex-block/superlatex-block-renderer.component';
import { TableBlockRendererComponent } from './block-renderers/table-block/table-block-renderer.component';
import { ToggleBlockRendererComponent } from './block-renderers/toggle-block/toggle-block-renderer.component';
import { WidgetBlockRendererComponent } from './block-renderers/widget-block/widget-block-renderer.component';
import { PopoverTriggerHostComponent } from './inline-renderers/popover-trigger-host/popover-trigger-host.component';

@Component({
  selector: 'dojo-content-blocks-renderer',
  template: `
    @for (block of blocks(); track block.id) {
      <div class="mb-4 last:mb-0" [ngClass]="block.custom_css ?? ''" [ngStyle]="block.custom_style ?? {}">
        @switch (block.type) {
          @case (ContentBlockType.Markdown) {
            <dojo-popover-trigger-host [block]="block" [contentRenderer]="selfComponent">
              <dojo-markdown-block-renderer [block]="block" />
            </dojo-popover-trigger-host>
          }
          @case (ContentBlockType.Callout) {
            <dojo-callout-block-renderer [block]="block" [contentRenderer]="selfComponent" />
          }
          @case (ContentBlockType.SimpleCallout) {
            <dojo-simple-callout-block-renderer [block]="block" [contentRenderer]="selfComponent" />
          }
          @case (ContentBlockType.Toggle) {
            <dojo-toggle-block-renderer [block]="block" [contentRenderer]="selfComponent" />
          }
          @case (ContentBlockType.Table) {
            <dojo-table-block-renderer [block]="block" [contentRenderer]="selfComponent" />
          }
          @case (ContentBlockType.SuperLatex) {
            <dojo-superlatex-block-renderer [block]="block" />
          }
          @case (ContentBlockType.Solver) {
            <dojo-solver-block-renderer [block]="block" />
          }
          @case (ContentBlockType.SimpleSolver) {
            <dojo-simple-solver-block-renderer [block]="block" />
          }
          @case (ContentBlockType.Widget) {
            <dojo-widget-block-renderer [block]="block" />
          }
          @case (ContentBlockType.Image) {
            <dojo-image-block-renderer [block]="block" />
          }
          @case (ContentBlockType.CustomComponent) {
            <dojo-custom-component-block-renderer [block]="block" />
          }
          @case (ContentBlockType.Accordion) {
            <dojo-accordion-block-renderer [block]="block" [contentRenderer]="selfComponent" />
          }
          @case (ContentBlockType.Columns) {
            <dojo-columns-block-renderer [block]="block" [contentRenderer]="selfComponent" />
          }
        }
      </div>
    }
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgStyle,
    AccordionBlockRendererComponent,
    ColumnsBlockRendererComponent,
    MarkdownBlockRendererComponent,
    CalloutBlockRendererComponent,
    SimpleCalloutBlockRendererComponent,
    ImageBlockRendererComponent,
    ToggleBlockRendererComponent,
    TableBlockRendererComponent,
    SuperLatexBlockRendererComponent,
    SolverBlockRendererComponent,
    SimpleSolverBlockRendererComponent,
    WidgetBlockRendererComponent,
    CustomComponentBlockRendererComponent,
    PopoverTriggerHostComponent,
  ],
  host: { class: 'block' },
})
export class ContentBlocksRendererComponent {
  readonly blocks = input.required<ContentBlock[]>();

  readonly ContentBlockType = ContentBlockType;
  readonly selfComponent = ContentBlocksRendererComponent;
}
